import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import queryString from 'qs';
import { Box } from '@mui/system';

import i18n from 'src/i18n_en';
import { trackLogIn } from 'src/utils/mixpanel';
import { authOperations } from 'src/store/modules/auth';
import 'src/components/SignIn/SignIn.scss';
import BeaconSignInContainer from 'src/components/SignIn/BeaconSignIn';
import SignInInputField from 'src/components/SignIn/SignInInputField';
import './SignUp.scss';
import fontStyle from 'src/utils/fontStyle';
import LoggingIn from './LoggingIn';
import ProfilePictureWithUpload from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Profile/ProfilePictureWithUpload';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import * as serverProxy from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/serverProxy';
import LogoContainer from '../SignIn/LogoContainer';
import AuthTitle from '../SignIn/AuthTitle';
import AuthContainer from '../SignIn/AuthContainer';
import AuthButton from '../SignIn/AuthButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { EMAIL_VALIDATOR_REGEX } from 'src/utils/validators';
import { AnimatedEllipsis } from 'src/components/common/Loading/AnimatedEllipsis';
import AuthTextButton from '../SignIn/AuthTextButton';
import { NewPictureAddPlusIcon } from '../SvgIcons/SvgIcons';
import { setAuthMethod } from 'src/utils/authVersion';

const styles = {
  tosWrapper: {
    width: 295,
    fontWeight: fontStyle.regularWeight,
    fontSize: 14,
    margin: 'auto',
    textAlign: 'left',
    color: '#fff'
  },
  signInButtonWrapper: {
    marginTop: 30,
    marginBottom: 16,
    textAlign: 'center'
  }
};

const SignUp = (props) => {
  const { app, auth, location, history, getUserByInviteCode, signUpByInvite, setErrorMessage, clearErrorMessage } =
    props;

  // State
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [buttonText, setButtonText] = useState(i18n.button_createAccount);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [onProfilePhotoPage, setOnProfilePhotoPage] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);

  // Component Did Mount
  useEffect(() => {
    const { code: inviteCode } = queryString.parse(location.search, { ignoreQueryPrefix: true, arrayLimit: 100 });

    getUserByInviteCode(typeof inviteCode === 'string' ? window.encodeURIComponent(inviteCode) : '')
      .then((response) => {
        if (response.data === 'signed') {
          history.push('/sign_in');
        } else {
          setCode(inviteCode ? String(inviteCode) : '');
          setEmail(response.data);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [getUserByInviteCode, history, location.search]);

  // Validation functions
  const validatePassword = React.useCallback((pwd) => {
    return pwd.length > 7;
  }, []);

  const handlePasswordChange = React.useCallback(
    (pwd) => {
      clearErrorMessage(null);
      setPassword(pwd);
    },
    [clearErrorMessage, setPassword]
  );

  const handlePasswordConfirmationChange = React.useCallback(
    (pwd) => {
      clearErrorMessage(null);
      setPasswordConfirmation(pwd);
    },
    [clearErrorMessage, setPasswordConfirmation]
  );

  const validateForm = React.useCallback(() => {
    const isEmailValid = EMAIL_VALIDATOR_REGEX.test(email);
    const isPasswordValid = password && validatePassword(password);
    const isPasswordConfirmationSame = password === passwordConfirmation;

    const isValid = isEmailValid && isPasswordConfirmationSame && isPasswordValid;
    if (!isValid) {
      if (!isPasswordConfirmationSame) {
        setErrorMessage("Passwords don't match. Please try again.");
      } else if (!isPasswordValid) {
        setErrorMessage('Password must be at least 8 characters.');
      }
    }
    return isValid;
  }, [email, password, passwordConfirmation, setErrorMessage, validatePassword]);

  const handleButtonTap = React.useCallback(
    async (requestBodyAdditionalParams = {}) => {
      setIsFormSubmitted(true);

      if (!validateForm()) {
        return;
      }

      setButtonText(i18n.button_creatingAccount);

      const requestBody = {
        code,
        email,
        password,
        confirmPassword: passwordConfirmation,
        agreed: true,
        ...requestBodyAdditionalParams
      };

      try {
        const response = await signUpByInvite(requestBody);
        const { status, redirect, redirectType } = response;

        if (status === 'bad') {
          setButtonText(i18n.button_createAccount);
        } else if (status === 'good') {
          setButtonText(i18n.button_accountCreated);

          if (redirectType === 'window') {
            setTimeout(() => {
              window.location.href = redirect;
            }, 1000);
            return;
          } else if (redirectType === 'local') {
            if (redirect === '/') {
              // Invite signup uses the V1 endpoint -> V1 cookies. Lock the
              // next mount to V1 and full-reload so AppSwitch picks the V1
              // shell on its first read (history.push would keep AuthShell
              // mounted; only main.tsx re-mounting can swap shells).
              setAuthMethod('v1');
              trackLogIn();
              window.location.href = redirect;
              return;
            }
          }
          trackLogIn();
        }
      } catch (err) {
        setButtonText(i18n.button_createAccount);
        console.error(err);
      }
    },
    [
      code,
      email,
      password,
      passwordConfirmation,
      history,
      signUpByInvite,
      validateForm,
      setButtonText,
      setIsFormSubmitted
    ]
  );

  const handleContinueToImageUpload = React.useCallback(() => {
    if (!validateForm()) {
      return;
    }
    setOnProfilePhotoPage(true);
  }, [validateForm, setOnProfilePhotoPage]);

  const handleProfilePhotoKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleButtonTap({ skipProfileImage: true });
      }
    },
    [handleButtonTap]
  );

  const { message } = auth;
  const Container = BeaconSignInContainer;

  if (onProfilePhotoPage) {
    return (
      <Container appName={app.name} notificationMSG={message}>
        <AuthContainer>
          <div onKeyDown={handleProfilePhotoKeyDown} style={{ width: '100%', outline: 'none' }} tabIndex={0} autoFocus>
            <LogoContainer title={app.displayName} />
            <p
              style={{
                fontSize: '34px',
                fontWeight: 600,
                marginTop: '16px',
                marginBottom: '40px',
                textAlign: 'center',
                fontFamily: 'Geist'
              }}
            >
              Upload profile photo
            </p>
            <Flex justifyContent="center" marginBottom="40px">
              <ProfilePictureWithUpload
                email={email}
                appProfileIconProps={{
                  size: 256,
                  backgroundColor: '#E6E9ED'
                }}
                fabSize={56}
                uploadImage={(file, userEmail) => {
                  setProfilePicture(file);
                  serverProxy.userPreSignedUrl(file, userEmail);
                  clearErrorMessage(null);
                  return Promise.resolve();
                }}
                fabIcon={<NewPictureAddPlusIcon style={{ width: '22px' }} />}
              />
            </Flex>
            <AuthButton
              type="button"
              onClick={() => {
                if (!profilePicture) {
                  setErrorMessage('Please upload a profile photo to complete your account setup.');
                  return;
                }
                handleButtonTap({ userDocUpload: true });
              }}
            >
              {isFormSubmitted ? (
                <Box display="flex" flexDirection="row" maxHeight="100%" alignItems="center" justifyContent="center">
                  {buttonText}
                  <span>...</span>
                </Box>
              ) : (
                <>{buttonText}</>
              )}
            </AuthButton>
            <AuthTextButton
              onClick={() => {
                handleButtonTap({ skipProfileImage: true });
              }}
            >
              Create account without photo
            </AuthTextButton>
          </div>
        </AuthContainer>
      </Container>
    );
  }

  return (
    <Container appName={app.name} notificationMSG={message}>
      <AuthContainer>
        <LogoContainer title={app.displayName} />

        <AuthTitle marginTop="16px" marginBottom="40px">
          Let&apos;s get started!
        </AuthTitle>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleContinueToImageUpload();
          }}
          style={{ width: '100%' }}
        >
          <Box display="flex" width="100%" flexDirection="column" gap="24px">
            {email && <SignInInputField label="Email" disabled value={email} />}
            <SignInInputField
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              label="Password"
              type="password"
              autoComplete="new-password"
              disabled={isFormSubmitted}
            />

            <SignInInputField
              value={passwordConfirmation}
              onChange={(e) => handlePasswordConfirmationChange(e.target.value)}
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
              disabled={isFormSubmitted}
            />
            <div className="signup-password-rules-container">
              <Text variant="subtitle2" sx={{ fontFamily: 'Geist', WebkitFontSmoothing: 'auto' }}>
                Passwords should have:
              </Text>
              <ul
                style={{
                  paddingLeft: '16px',
                  marginTop: '8px',
                  fontSize: '14px',
                  fontWeight: fontStyle.regularWeight,
                  fontFamily: 'Geist'
                }}
              >
                <li>at least 8 characters</li>
                <li style={{ marginTop: '4px' }}>at least one lowercase letter</li>
              </ul>
            </div>
          </Box>

          <div style={{ marginTop: 30, marginBottom: 16, textAlign: 'center' }}>
            <AuthButton type="submit">Continue</AuthButton>
          </div>
        </form>
      </AuthContainer>
    </Container>
  );
};

SignUp.propTypes = {
  app: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  getUserByInviteCode: PropTypes.func.isRequired,
  signUpByInvite: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  clearErrorMessage: PropTypes.func.isRequired
};

const mapStateToProps = ({ app, auth }) => ({ app, auth });

const mapDispatchToProps = {
  getUserByInviteCode: authOperations.getUserByInviteCode,
  signUpByInvite: authOperations.signUpByInvite,
  setErrorMessage: authOperations.setErrorMessage,
  clearErrorMessage: authOperations.clearMessage
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SignUp));
